/**
 * What to tell the user when STARTING a coaching conversation was refused.
 *
 * ── SAY WHAT ACTUALLY HAPPENED ──────────────────────────────────────────────
 * The server distinguishes these outcomes; flattening them into one message
 * threw that away. With an empty wallet the API answers
 *   402 {"error":{"code":"INSUFFICIENT_CREDITS"}}
 * and the user was told "Failed to start coaching session. Please try again."
 * — advice that cannot work. RC19: a 403 PLAN_LIMIT (e.g. the Free plan's
 * maxDailyPracticeSessions, "You've used 3 of 3…") got the same generic
 * "try again"; it now shows the server's limit copy (planLimitNotice, as
 * PracticePage does), and the 429 safety cap and 503 codes read the shared
 * attempt-error map. A rolling limit is never answered with "try again".
 */
import { planLimitNotice } from './errorHelpers';
import { ATTEMPT_ERROR_COPY, attemptErrorView } from './attemptErrors';

/** The server's own message for a refusal it explains (422 CONTENT_INCOMPLETE etc.). */
export function serverMessage(error: unknown): string | null {
  const data = (error as { data?: { message?: unknown } })?.data;
  return typeof data?.message === 'string' && data.message.length > 0 ? data.message : null;
}

/**
 * The out-of-credits refusal (Central's credit reservation answered 402). The
 * notice also offers Add Credits / "ask your owner" (AddCreditsAction).
 */
export const INSUFFICIENT_CREDITS_COPY =
  'You do not have enough credits to start this session. Open Manage billing (in Settings → Billing) to review your plan and credits.';

/** True for the out-of-credits refusal — not a plan limit, not the safety cap. */
export function isInsufficientCreditsError(error: unknown): boolean {
  const code = (error as { data?: { error?: { code?: string } } })?.data?.error?.code;
  const status = (error as { status?: number })?.status;
  if (planLimitNotice(error) || code === 'CONVERSATION_SAFETY_LIMIT') return false;
  return code === 'INSUFFICIENT_CREDITS' || status === 402;
}

export function coachingStartErrorMessage(
  error: unknown,
  opts: { languageLearning: boolean; levelUnavailableNote: string; gradedUnavailableNote: string }
): string {
  const code = (error as { data?: { error?: { code?: string } }; status?: number })?.data?.error?.code;
  const status = (error as { status?: number })?.status;

  // A plan limit: the server's own limit copy, which says what counts and when it frees up.
  const planLimit = planLimitNotice(error);
  if (planLimit) return planLimit.message;
  // 429 every-plan safety cap: the server's copy (with its number), else the shared one.
  if (code === 'CONVERSATION_SAFETY_LIMIT') {
    return serverMessage(error) ?? ATTEMPT_ERROR_COPY.CONVERSATION_SAFETY_LIMIT.message;
  }
  if (isInsufficientCreditsError(error)) return INSUFFICIENT_CREDITS_COPY;
  if (code === 'SONG_NOT_AVAILABLE') return 'This song is not available for practice.';
  if (code === 'CONTENT_INCOMPLETE') {
    return opts.languageLearning
      ? serverMessage(error) ?? 'This language practice has not been authored yet, so it cannot be started.'
      : 'This interview practice has not been authored yet, so it cannot be started.';
  }
  // Engine off: the legacy runtime cannot hold a level attempt.
  if (code === 'LEVEL_CONVERSATION_UNAVAILABLE') return serverMessage(error) ?? opts.levelUnavailableNote;
  if (code === 'GRADED_CONVERSATION_UNAVAILABLE') return serverMessage(error) ?? opts.gradedUnavailableNote;
  // Known codes (CENTRAL_UNAVAILABLE, USAGE_AUTHORITY_UNAVAILABLE, busy…): worded once, in utils/attemptErrors.
  if (code && ATTEMPT_ERROR_COPY[code]) return attemptErrorView(error, '').message;
  if (status === 503) return 'Sessions are temporarily unavailable. Please try again shortly.';
  // A refusal the server explains: Testing without authored content, or a
  // level that is locked or belongs to another persona. Retrying will not help.
  if (status === 422) return serverMessage(error) ?? 'This conversation cannot be started with the selected content.';
  return 'Failed to start coaching session. Please try again.';
}
